import { useEffect, useState } from 'react';

export default function MarkerScribbles() {
  const [scribbles, setScribbles] = useState<Array<{id: string, x: number, y: number, rotation: number, scale: number, opacity: number}>>([]);
  const [glitchActive, setGlitchActive] = useState(false);

  useEffect(() => {
    // Generate random scribbles
    const generateScribbles = () => {
      const newScribbles = Array.from({ length: 8 }, (_, i) => ({
        id: `scribble-${i}`,
        x: Math.random() * window.innerWidth,
        y: Math.random() * window.innerHeight,
        rotation: Math.random() * 360,
        scale: 0.5 + Math.random() * 1.5,
        opacity: 0.3 + Math.random() * 0.4
      }));
      setScribbles(newScribbles);
    };

    generateScribbles();
    
    // Randomly trigger glitch effect on scribbles
    const glitchInterval = setInterval(() => {
      if (Math.random() > 0.8) {
        setGlitchActive(true);
        setTimeout(() => setGlitchActive(false), 200);
      }
    }, 1500);

    // Regenerate scribbles occasionally
    const regenerateInterval = setInterval(generateScribbles, 10000);

    window.addEventListener('resize', generateScribbles);

    return () => {
      clearInterval(glitchInterval);
      clearInterval(regenerateInterval);
      window.removeEventListener('resize', generateScribbles);
    };
  }, []);

  const scribblePaths = [
    "M10,30 Q20,10 30,30 T50,30 Q60,40 70,20",
    "M5,20 C15,5 25,35 35,20 S55,5 65,20",
    "M10,25 Q30,5 50,25 Q70,45 90,25",
    "M15,35 C25,15 35,55 45,35 C55,15 65,55 75,35",
    "M20,30 Q40,10 60,30 Q80,50 100,30",
    "M5,25 S25,5 45,25 S65,45 85,25",
    "M12,28 Q32,8 52,28 Q72,48 92,28",
    "M8,32 C28,12 48,52 68,32 C88,12 108,52 128,32"
  ];

  return (
    <div className="fixed inset-0 pointer-events-none z-10 overflow-hidden">
      {scribbles.map((scribble, index) => (
        <div
          key={scribble.id}
          className={`absolute transition-all duration-300 ${
            glitchActive ? 'animate-intense-glitch' : ''
          }`}
          style={{
            left: scribble.x,
            top: scribble.y,
            transform: `rotate(${scribble.rotation}deg) scale(${scribble.scale})`,
            opacity: scribble.opacity,
            filter: glitchActive ? `hue-rotate(${Math.random() * 360}deg) contrast(2)` : 'none'
          }}
        >
          <svg 
            width="120" 
            height="60" 
            viewBox="0 0 120 60"
            className={glitchActive ? 'animate-rgb-split' : ''}
          >
            <path
              d={scribblePaths[index % scribblePaths.length]}
              stroke="rgba(255, 255, 255, 0.6)"
              strokeWidth="2"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{
                filter: 'drop-shadow(0 0 4px rgba(255, 255, 255, 0.8))',
                strokeDasharray: '5,3',
                strokeDashoffset: glitchActive ? Math.random() * 10 : 0
              }}
            />
            <path
              d={scribblePaths[index % scribblePaths.length]}
              stroke="rgba(0, 255, 255, 0.4)"
              strokeWidth="1"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{
                transform: `translate(${glitchActive ? Math.random() * 4 - 2 : 0}px, ${glitchActive ? Math.random() * 4 - 2 : 0}px)`,
                filter: 'blur(0.5px)'
              }}
            />
          </svg>
        </div>
      ))}
      
      {/* Additional chaotic scribbles that appear during glitch */}
      {glitchActive && (
        <>
          {Array.from({ length: 3 }, (_, i) => (
            <div
              key={`chaos-${i}`}
              className="absolute animate-data-corruption"
              style={{
                left: Math.random() * window.innerWidth,
                top: Math.random() * window.innerHeight,
                transform: `rotate(${Math.random() * 360}deg) scale(${0.3 + Math.random() * 0.7})`,
              }}
            >
              <svg width="80" height="40" viewBox="0 0 80 40">
                <path
                  d="M5,20 Q20,5 35,20 Q50,35 65,20 Q80,5 95,20"
                  stroke="rgba(255, 0, 255, 0.8)"
                  strokeWidth="2"
                  fill="none"
                  strokeLinecap="round"
                  style={{
                    filter: 'drop-shadow(0 0 6px rgba(255, 0, 255, 1))'
                  }}
                />
              </svg>
            </div>
          ))}
        </>
      )}
    </div>
  );
}